import { useEffect, useState, useCallback, useRef } from "react";
import api, { formatApiError } from "@/lib/api";
import { PageHeader } from "@/components/PageHeader";
import { Card } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { Badge } from "@/components/ui/badge";
import {
  Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter, DialogDescription, DialogTrigger,
} from "@/components/ui/dialog";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { useAuth } from "@/context/AuthContext";
import { KnowledgeFilesPanel } from "@/components/KnowledgeFilesPanel";
import {
  Brain, Plus, Search, Upload, Loader2, Trash2, Edit3, BookOpen, Sparkles, ShieldAlert, Clock, Wrench, Zap, CheckCircle2,
} from "lucide-react";
import { toast } from "sonner";

const severityColor = {
  bassa: "bg-zinc-50 text-zinc-700 border-zinc-200",
  media: "bg-zinc-100 text-zinc-800 border-zinc-200",
  alta: "bg-amber-100 text-amber-800 border-amber-200",
  critica: "bg-red-100 text-red-800 border-red-200",
};

const sourceLabel = {
  manual: "Manuale",
  auto_from_ticket: "Da ticket risolto",
  import: "Import",
  template: "Da template",
};

const emptyEntry = {
  title: "",
  symptom: "",
  models: "",
  probable_causes: "",
  resolution: "",
  part_codes: "",
  severity: "media",
  estimated_hours: 1,
  safety_notes: "",
  tags: "",
};

function ReadinessBar({ stats }) {
  const pct = stats?.readiness_pct ?? 0;
  const ready = stats?.is_ready;
  const barColor = ready ? "bg-emerald-500" : pct >= 50 ? "bg-amber-500" : "bg-brand";
  return (
    <Card className="rounded-sm border-zinc-200 bg-white p-4 sm:p-5" data-testid="kb-readiness">
      <div className="flex flex-col sm:flex-row sm:items-start sm:justify-between gap-3 mb-3">
        <div className="min-w-0">
          <div className="flex items-center gap-2">
            <Brain className="h-4 w-4 text-brand" strokeWidth={1.75} />
            <div className="text-[10px] uppercase tracking-[0.2em] text-zinc-500">Livello di apprendimento AI</div>
          </div>
          <div className="mt-2 font-display text-3xl font-bold tracking-tighter text-zinc-950">
            {stats?.total ?? 0} <span className="text-lg font-normal text-zinc-400">/ {stats?.threshold ?? 20}</span>
          </div>
          <div className="text-xs text-zinc-600 mt-1">
            {ready ? (
              <span className="text-emerald-700 font-medium">AI pronta a suggerire ricambi e soluzioni</span>
            ) : (
              <span>Ancora {Math.max(0, (stats?.threshold ?? 20) - (stats?.total ?? 0))} entry per attivare i suggerimenti automatici</span>
            )}
          </div>
        </div>
        <div className="flex flex-row sm:flex-col items-start sm:items-end gap-2 sm:gap-1 flex-wrap">
          <Badge className={`rounded-sm text-[10px] uppercase tracking-widest border ${ready ? "bg-emerald-50 text-emerald-800 border-emerald-200" : "bg-amber-50 text-amber-800 border-amber-200"} whitespace-nowrap`} data-testid="kb-status-badge">
            {ready ? "AI ATTIVA" : "IN APPRENDIMENTO"}
          </Badge>
          <div className="text-[10px] text-zinc-500 whitespace-nowrap">
            {stats?.manual ?? 0} manuali · {stats?.auto_from_ticket ?? 0} da ticket · {stats?.imported ?? 0} import
          </div>
        </div>
      </div>
      <div className="h-2 w-full bg-zinc-100 rounded-sm overflow-hidden">
        <div
          className={`h-full ${barColor} transition-all duration-500`}
          style={{ width: `${Math.min(100, pct)}%` }}
          data-testid="kb-progress-bar"
        />
      </div>
    </Card>
  );
}

function EntryDialog({ open, onOpenChange, entry, onSaved }) {
  const [form, setForm] = useState(emptyEntry);
  const [saving, setSaving] = useState(false);
  const isEdit = !!entry?.id;

  useEffect(() => {
    if (open) {
      if (entry) {
        setForm({
          title: entry.title || "",
          symptom: entry.symptom || "",
          models: (entry.models || []).join(", "),
          probable_causes: entry.probable_causes || "",
          resolution: entry.resolution || "",
          part_codes: (entry.part_codes || []).join(", "),
          severity: entry.severity || "media",
          estimated_hours: entry.estimated_hours ?? 1,
          safety_notes: entry.safety_notes || "",
          tags: (entry.tags || []).join(", "),
        });
      } else setForm(emptyEntry);
    }
  }, [open, entry]);

  const submit = async () => {
    if (!form.title.trim() || !form.symptom.trim()) {
      toast.error("Titolo e sintomo sono obbligatori");
      return;
    }
    setSaving(true);
    const payload = {
      title: form.title.trim(),
      symptom: form.symptom.trim(),
      models: form.models.split(",").map((s) => s.trim()).filter(Boolean),
      probable_causes: form.probable_causes || null,
      resolution: form.resolution || null,
      part_codes: form.part_codes.split(",").map((s) => s.trim()).filter(Boolean),
      severity: form.severity,
      estimated_hours: parseFloat(form.estimated_hours) || 1,
      safety_notes: form.safety_notes || null,
      tags: form.tags.split(",").map((s) => s.trim()).filter(Boolean),
    };
    try {
      if (isEdit) await api.put(`/knowledge/${entry.id}`, payload);
      else await api.post("/knowledge", payload);
      toast.success(isEdit ? "Entry aggiornata" : "Entry creata");
      onSaved?.();
      onOpenChange(false);
    } catch (e) { toast.error(formatApiError(e)); }
    finally { setSaving(false); }
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="rounded-sm max-w-2xl max-h-[90vh] overflow-y-auto">
        <DialogHeader>
          <DialogTitle className="font-display tracking-tight">
            {isEdit ? "Modifica entry" : "Nuova entry di conoscenza"}
          </DialogTitle>
          <DialogDescription className="text-xs text-zinc-500">
            Verrà usata dall&apos;AI per suggerire cause probabili, procedure e ricambi.
          </DialogDescription>
        </DialogHeader>
        <div className="space-y-3">
          <div>
            <Label className="text-xs uppercase tracking-widest text-zinc-500">Titolo *</Label>
            <Input data-testid="kb-form-title" value={form.title} onChange={(e) => setForm({ ...form, title: e.target.value })} className="mt-1 rounded-sm" placeholder="es. Pressione azoto insufficiente" />
          </div>
          <div>
            <Label className="text-xs uppercase tracking-widest text-zinc-500">Sintomo osservato *</Label>
            <Textarea data-testid="kb-form-symptom" rows={2} value={form.symptom} onChange={(e) => setForm({ ...form, symptom: e.target.value })} className="mt-1 rounded-sm" placeholder="Cosa vede/sente il cliente..." />
          </div>
          <div className="grid grid-cols-2 gap-3">
            <div>
              <Label className="text-xs uppercase tracking-widest text-zinc-500">Modelli compatibili</Label>
              <Input data-testid="kb-form-models" value={form.models} onChange={(e) => setForm({ ...form, models: e.target.value })} className="mt-1 rounded-sm" placeholder="Enobox A1, D. OTTO 8 BOTT" />
              <div className="text-[10px] text-zinc-500 mt-1">Separati da virgola</div>
            </div>
            <div>
              <Label className="text-xs uppercase tracking-widest text-zinc-500">Codici ricambio</Label>
              <Input data-testid="kb-form-parts" value={form.part_codes} onChange={(e) => setForm({ ...form, part_codes: e.target.value })} className="mt-1 rounded-sm font-mono" placeholder="SPR0193, SPR0214" />
            </div>
          </div>
          <div>
            <Label className="text-xs uppercase tracking-widest text-zinc-500">Cause probabili</Label>
            <Textarea data-testid="kb-form-causes" rows={2} value={form.probable_causes} onChange={(e) => setForm({ ...form, probable_causes: e.target.value })} className="mt-1 rounded-sm" placeholder="Elenco cause in ordine di probabilità..." />
          </div>
          <div>
            <Label className="text-xs uppercase tracking-widest text-zinc-500">Procedura di risoluzione</Label>
            <Textarea data-testid="kb-form-resolution" rows={4} value={form.resolution} onChange={(e) => setForm({ ...form, resolution: e.target.value })} className="mt-1 rounded-sm" placeholder="1. Verificare X. 2. Testare Y. 3. Sostituire Z..." />
          </div>
          <div className="grid grid-cols-3 gap-3">
            <div>
              <Label className="text-xs uppercase tracking-widest text-zinc-500">Gravità</Label>
              <Select value={form.severity} onValueChange={(v) => setForm({ ...form, severity: v })}>
                <SelectTrigger data-testid="kb-form-severity" className="mt-1 rounded-sm"><SelectValue /></SelectTrigger>
                <SelectContent>
                  <SelectItem value="bassa">Bassa</SelectItem>
                  <SelectItem value="media">Media</SelectItem>
                  <SelectItem value="alta">Alta</SelectItem>
                  <SelectItem value="critica">Critica</SelectItem>
                </SelectContent>
              </Select>
            </div>
            <div>
              <Label className="text-xs uppercase tracking-widest text-zinc-500">Ore stimate</Label>
              <Input data-testid="kb-form-hours" type="number" step="0.25" min="0" value={form.estimated_hours} onChange={(e) => setForm({ ...form, estimated_hours: e.target.value })} className="mt-1 rounded-sm" />
            </div>
            <div>
              <Label className="text-xs uppercase tracking-widest text-zinc-500">Tag</Label>
              <Input data-testid="kb-form-tags" value={form.tags} onChange={(e) => setForm({ ...form, tags: e.target.value })} className="mt-1 rounded-sm" placeholder="pompa, azoto..." />
            </div>
          </div>
          <div>
            <Label className="text-xs uppercase tracking-widest text-zinc-500">Note di sicurezza</Label>
            <Input data-testid="kb-form-safety" value={form.safety_notes} onChange={(e) => setForm({ ...form, safety_notes: e.target.value })} className="mt-1 rounded-sm" placeholder="es. Disconnettere corrente prima dell'intervento" />
          </div>
        </div>
        <DialogFooter>
          <Button variant="outline" className="rounded-sm" onClick={() => onOpenChange(false)}>Annulla</Button>
          <Button data-testid="kb-form-submit" onClick={submit} disabled={saving} className="bg-brand hover:bg-brand-dark text-white rounded-sm">
            {saving ? <><Loader2 className="h-4 w-4 mr-2 animate-spin" /> Salvataggio...</> : (isEdit ? "Aggiorna" : "Crea entry")}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}

function ImportButton({ onDone }) {
  const inputRef = useRef();
  const [busy, setBusy] = useState(false);
  const submit = async (file) => {
    if (!file) return;
    setBusy(true);
    const fd = new FormData();
    fd.append("file", file);
    try {
      const { data } = await api.post("/import/knowledge", fd, { headers: { "Content-Type": "multipart/form-data" } });
      toast.success(`${data.inserted} inserite, ${data.updated} aggiornate`);
      onDone?.();
    } catch (e) { toast.error(formatApiError(e)); }
    finally { setBusy(false); if (inputRef.current) inputRef.current.value = ""; }
  };
  return (
    <>
      <input ref={inputRef} type="file" accept=".csv,.xlsx" className="hidden" data-testid="kb-import-input" onChange={(e) => submit(e.target.files?.[0])} />
      <Button variant="outline" className="rounded-sm" onClick={() => inputRef.current?.click()} disabled={busy} data-testid="kb-import-btn">
        {busy ? <><Loader2 className="h-4 w-4 mr-2 animate-spin" /> Import...</> : <><Upload className="h-4 w-4 mr-2" /> <span className="hidden sm:inline">Import CSV/XLSX</span><span className="sm:hidden">Import</span></>}
      </Button>
    </>
  );
}

function TemplatesDialog({ open, onOpenChange, onCreated }) {
  const [templates, setTemplates] = useState([]);
  const [loading, setLoading] = useState(false);
  const [creating, setCreating] = useState(null);

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const { data } = await api.get("/knowledge/templates");
      setTemplates(data);
    } catch (e) { toast.error(formatApiError(e)); }
    finally { setLoading(false); }
  }, []);

  useEffect(() => { if (open) load(); }, [open, load]);

  const apply = async (t) => {
    setCreating(t.id);
    try {
      await api.post(`/knowledge/from-template/${t.id}`);
      toast.success(`Entry "${t.title}" creata dal template`);
      await load();
      onCreated?.();
    } catch (e) { toast.error(formatApiError(e)); }
    finally { setCreating(null); }
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="rounded-sm max-w-4xl max-h-[90vh] overflow-y-auto">
        <DialogHeader>
          <DialogTitle className="font-display tracking-tight flex items-center gap-2">
            <Zap className="h-5 w-5 text-brand" /> Template guasti tipici
          </DialogTitle>
          <DialogDescription className="text-xs text-zinc-500">
            {templates.length} template pronti per i dispenser Wineemotion. Click su &quot;Aggiungi&quot; per creare una entry KB modificabile.
          </DialogDescription>
        </DialogHeader>
        <div className="space-y-2">
          {loading && <div className="text-sm text-zinc-500 text-center py-6">Caricamento...</div>}
          {!loading && templates.map((t) => (
            <div key={t.id} className={`flex items-start gap-3 rounded-sm border p-3 ${t.already_used ? "border-emerald-200 bg-emerald-50/30" : "border-zinc-200 hover:bg-zinc-50"}`} data-testid={`template-${t.id}`}>
              <div className={`h-8 w-8 shrink-0 rounded-sm flex items-center justify-center ${t.already_used ? "bg-emerald-100 text-emerald-700" : "bg-brand-muted text-brand"}`}>
                {t.already_used ? <CheckCircle2 className="h-4 w-4" /> : <Sparkles className="h-4 w-4" />}
              </div>
              <div className="flex-1 min-w-0">
                <div className="flex items-center gap-2 flex-wrap">
                  <div className="font-display font-semibold text-sm text-zinc-950">{t.title}</div>
                  <Badge className={`rounded-sm text-[10px] uppercase tracking-widest border ${severityColor[t.severity] || severityColor.media}`}>{t.severity}</Badge>
                  <span className="text-[10px] text-zinc-500">{t.estimated_hours}h</span>
                </div>
                <div className="text-xs text-zinc-600 mt-1 line-clamp-2">{t.symptom}</div>
                <div className="flex items-center flex-wrap gap-1 mt-2">
                  {t.tags.slice(0, 4).map((tag) => (
                    <span key={tag} className="text-[10px] uppercase tracking-widest bg-zinc-100 text-zinc-600 rounded-sm px-1.5 py-0.5">{tag}</span>
                  ))}
                </div>
              </div>
              <Button
                size="sm"
                data-testid={`template-add-${t.id}`}
                disabled={creating === t.id}
                onClick={() => apply(t)}
                variant={t.already_used ? "outline" : "default"}
                className={`h-8 rounded-sm text-xs shrink-0 ${t.already_used ? "" : "bg-brand hover:bg-brand-dark text-white"}`}
              >
                {creating === t.id ? <Loader2 className="h-3.5 w-3.5 animate-spin" /> : (t.already_used ? "Aggiungi ancora" : "Aggiungi")}
              </Button>
            </div>
          ))}
        </div>
      </DialogContent>
    </Dialog>
  );
}

export default function KnowledgeBase() {
  const { user } = useAuth();
  const [entries, setEntries] = useState([]);
  const [stats, setStats] = useState(null);
  const [q, setQ] = useState("");
  const [sourceFilter, setSourceFilter] = useState("all");
  const [dialogOpen, setDialogOpen] = useState(false);
  const [templatesOpen, setTemplatesOpen] = useState(false);
  const [editing, setEditing] = useState(null);
  const [loading, setLoading] = useState(false);

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const params = {};
      if (q) params.q = q;
      if (sourceFilter !== "all") params.source = sourceFilter;
      const [e, s] = await Promise.all([
        api.get("/knowledge", { params }),
        api.get("/knowledge/stats"),
      ]);
      setEntries(e.data);
      setStats(s.data);
    } catch (er) { toast.error(formatApiError(er)); }
    finally { setLoading(false); }
  }, [q, sourceFilter]);

  useEffect(() => { load(); }, [load]);

  const del = async (id) => {
    if (!window.confirm("Eliminare questa entry?")) return;
    try {
      await api.delete(`/knowledge/${id}`);
      toast.success("Entry eliminata");
      load();
    } catch (e) { toast.error(formatApiError(e)); }
  };

  const openNew = () => { setEditing(null); setDialogOpen(true); };
  const openEdit = (entry) => { setEditing(entry); setDialogOpen(true); };

  return (
    <div className="p-4 sm:p-8 max-w-7xl">
      <PageHeader
        title="Base di Conoscenza"
        description="Casi risolti, procedure e ricambi tipici. Alimenta l'assistente AI: più entry, più accurati i suggerimenti."
        actions={
          <div className="flex items-center gap-2 flex-wrap w-full sm:w-auto">
            <Button
              data-testid="kb-templates-btn"
              variant="outline"
              onClick={() => setTemplatesOpen(true)}
              className="rounded-sm border-brand/30 text-brand hover:bg-brand-muted"
            >
              <Zap className="h-4 w-4 mr-2" /> <span className="hidden sm:inline">Template guasti tipici</span><span className="sm:hidden">Template</span>
            </Button>
            {user?.role === "admin" && <ImportButton onDone={load} />}
            <Button data-testid="kb-new-btn" onClick={openNew} className="bg-brand hover:bg-brand-dark text-white rounded-sm">
              <Plus className="h-4 w-4 mr-2" /> <span className="hidden sm:inline">Nuova entry</span><span className="sm:hidden">Nuova</span>
            </Button>
          </div>
        }
      />

      <div className="mb-6">
        <ReadinessBar stats={stats} />
      </div>

      <div className="mb-6">
        <KnowledgeFilesPanel isAdmin={user?.role === "admin"} />
      </div>

      <div className="flex items-center gap-2 mb-4 flex-wrap">
        <div className="relative flex-1 max-w-md">
          <Search className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-zinc-400" />
          <Input data-testid="kb-search" value={q} onChange={(e) => setQ(e.target.value)} placeholder="Cerca per sintomo, causa, soluzione..." className="pl-9 rounded-sm h-10" />
        </div>
        <Select value={sourceFilter} onValueChange={setSourceFilter}>
          <SelectTrigger data-testid="kb-source-filter" className="w-56 h-10 rounded-sm text-sm"><SelectValue /></SelectTrigger>
          <SelectContent>
            <SelectItem value="all">Tutte le origini</SelectItem>
            <SelectItem value="manual">Manuali</SelectItem>
            <SelectItem value="template">Da template</SelectItem>
            <SelectItem value="auto_from_ticket">Da ticket risolti</SelectItem>
            <SelectItem value="import">Da import</SelectItem>
          </SelectContent>
        </Select>
      </div>

      <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
        {loading && <div className="col-span-full text-sm text-zinc-500 text-center py-10">Caricamento...</div>}
        {!loading && entries.length === 0 && (
          <Card className="col-span-full rounded-sm border-zinc-200 bg-white p-12 text-center">
            <BookOpen className="h-10 w-10 mx-auto mb-3 text-zinc-300" strokeWidth={1.25} />
            <div className="text-sm text-zinc-600">Nessuna entry di conoscenza.</div>
            <div className="text-xs text-zinc-500 mt-1">Crea la prima entry o marca un ticket risolto come esempio di apprendimento.</div>
          </Card>
        )}
        {!loading && entries.map((e) => (
          <Card key={e.id} className="rounded-sm border-zinc-200 bg-white p-5" data-testid={`kb-entry-${e.id}`}>
            <div className="flex items-start justify-between gap-3 mb-2">
              <div className="min-w-0 flex-1">
                <h3 className="font-display text-lg font-semibold text-zinc-950 tracking-tight leading-tight">{e.title}</h3>
                <div className="flex items-center flex-wrap gap-1.5 mt-2">
                  <Badge className={`rounded-sm text-[10px] uppercase tracking-widest border ${severityColor[e.severity] || severityColor.media}`}>{e.severity}</Badge>
                  <Badge className="rounded-sm text-[10px] uppercase tracking-widest border bg-zinc-50 text-zinc-600 border-zinc-200">
                    {sourceLabel[e.source_type] || e.source_type}
                  </Badge>
                  {e.models?.slice(0, 2).map((m) => <Badge key={m} className="rounded-sm text-[10px] border bg-brand-muted text-brand border-brand/20">{m}</Badge>)}
                  {e.models?.length > 2 && <span className="text-[10px] text-zinc-500">+{e.models.length - 2}</span>}
                </div>
              </div>
              <div className="flex items-center gap-1 shrink-0">
                <button onClick={() => openEdit(e)} className="h-8 w-8 rounded-sm border border-zinc-300 text-zinc-600 hover:bg-zinc-50 flex items-center justify-center" data-testid={`kb-edit-${e.id}`}><Edit3 className="h-3.5 w-3.5" /></button>
                {user?.role === "admin" && (
                  <button onClick={() => del(e.id)} className="h-8 w-8 rounded-sm border border-zinc-300 text-zinc-400 hover:text-red-600 hover:border-red-200 flex items-center justify-center" data-testid={`kb-delete-${e.id}`}><Trash2 className="h-3.5 w-3.5" /></button>
                )}
              </div>
            </div>
            <div className="text-xs text-zinc-500 uppercase tracking-widest mt-3 mb-1">Sintomo</div>
            <div className="text-sm text-zinc-800 line-clamp-2">{e.symptom}</div>
            {e.resolution && (
              <>
                <div className="text-xs text-zinc-500 uppercase tracking-widest mt-3 mb-1">Risoluzione</div>
                <div className="text-sm text-zinc-700 line-clamp-3 whitespace-pre-wrap">{e.resolution}</div>
              </>
            )}
            <div className="flex items-center gap-3 mt-3 pt-3 border-t border-zinc-100 text-[11px] text-zinc-600">
              {e.part_codes?.length > 0 && (
                <div className="flex items-center gap-1">
                  <Wrench className="h-3 w-3" />
                  <span className="font-mono">{e.part_codes.slice(0, 3).join(", ")}{e.part_codes.length > 3 ? ` +${e.part_codes.length - 3}` : ""}</span>
                </div>
              )}
              <div className="flex items-center gap-1"><Clock className="h-3 w-3" /> {e.estimated_hours}h</div>
              {e.safety_notes && <div className="flex items-center gap-1 text-amber-700"><ShieldAlert className="h-3 w-3" /> sicurezza</div>}
            </div>
          </Card>
        ))}
      </div>

      <EntryDialog open={dialogOpen} onOpenChange={setDialogOpen} entry={editing} onSaved={load} />
      <TemplatesDialog open={templatesOpen} onOpenChange={setTemplatesOpen} onCreated={load} />
    </div>
  );
}
